import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import type { PolicyInput } from 'used-vehicle-contracts';
import type { BuildInputsResult, RunResult, UnitName } from 'used-vehicle-engine';
import { Badge } from '../../../components/ui/Badge';
import { DefinitionList } from '../../../components/ui/DefinitionList';
import { FormField } from '../../../components/ui/FormField';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { formatEquationValue } from './equations-display';

export interface SampleFacts {
  valuationYear: number;
  modelYear: number;
  odometerKm: number;
  ownerCount: number;
}

/** A template whose published values the sample can be priced with (plan §10, phase-1 hook). */
export interface SampleValuesOption {
  templateId: string;
  versionId: string;
  label: string;
  version: number;
}

export interface SamplePreviewCardProps {
  sample: SampleFacts;
  onSampleChange: (sample: SampleFacts) => void;
  run: RunResult;
  built: BuildInputsResult;
  inputs: readonly PolicyInput[];
  /** Templates with a published version; the built-in reference sample is always the first option. */
  valuesOptions: readonly SampleValuesOption[];
  /** `''` = the built-in reference values. */
  valuesFrom: string;
  onValuesFromChange: (templateId: string) => void;
  /** The values actually in use, once the chosen template's version has loaded. */
  activeValues: SampleValuesOption | null;
}

/** The figures an administrator watches while editing: the appraisal chain and three fee examples. */
const KEY_OUTPUTS: readonly { id: string; unit: UnitName }[] = [
  { id: 'age', unit: 'years' },
  { id: 'appraisal', unit: 'JOD' },
  { id: 'ceiling', unit: 'JOD' },
  { id: 'retail', unit: 'JOD' },
  { id: 'insurance', unit: 'JOD' },
  { id: 'registration', unit: 'JOD' },
  { id: 'transfer', unit: 'JOD' },
];

/**
 * The built-in sample: the product plan's reference vehicle, priced live by whatever the administrator
 * has typed (plan §7, PP §10). It runs entirely in the browser through the engine package — the same
 * code the service uses — so there is no request between a keystroke and a new number.
 */
export const SamplePreviewCard = ({
  sample,
  onSampleChange,
  run,
  built,
  inputs,
  valuesOptions,
  valuesFrom,
  onValuesFromChange,
  activeValues,
}: SamplePreviewCardProps) => {
  const { t } = useI18nTranslation();
  const [isInputsOpen, setIsInputsOpen] = useState(false);

  const unitLabel = (unit: UnitName): string => t(`equations.units.${unit}`);

  const numberField = (key: keyof SampleFacts, label: string, min: number, max: number) => (
    <FormField
      id={`sample-${key}`}
      label={label}
    >
      <Input
        id={`sample-${key}`}
        type="number"
        dir="ltr"
        min={min}
        max={max}
        value={String(sample[key])}
        className="tabular-nums"
        onChange={(event) => {
          const parsed = Number(event.target.value);

          onSampleChange({ ...sample, [key]: Number.isFinite(parsed) ? parsed : sample[key] });
        }}
      />
    </FormField>
  );

  const ageTrace = run.trace.age;

  return (
    <div className="space-y-4 lg:sticky lg:top-6">
      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div>
          <h2 className="text-sm font-semibold text-card-foreground">{t('equations.sampleTitle')}</h2>
          <p className="mt-1 text-xs text-muted-foreground">{t('equations.sampleSubtitle')}</p>
        </div>

        <FormField
          id="sample-values-from"
          label={t('equations.sampleValuesFrom')}
          hint={t('equations.sampleValuesFromHint')}
        >
          <Select
            id="sample-values-from"
            value={valuesFrom}
            onChange={(event) => onValuesFromChange(event.target.value)}
            options={[
              { value: '', label: t('equations.sampleValuesBuiltIn') },
              ...valuesOptions.map((option) => ({ value: option.templateId, label: option.label })),
            ]}
          />
        </FormField>

        {activeValues ? (
          <Badge tone="info">{t('equations.sampleValuesBadge', { name: activeValues.label, version: activeValues.version })}</Badge>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          {numberField('valuationYear', t('equations.sampleValuationYear'), 1900, 2999)}
          {numberField('modelYear', t('equations.sampleModelYear'), 1900, 2999)}
          {numberField('odometerKm', t('equations.sampleOdometer'), 0, 2_000_000)}
          {numberField('ownerCount', t('equations.sampleOwners'), 0, 50)}
        </div>

        <div>
          <p className="mb-1.5 text-xs font-medium text-muted-foreground">{t('equations.sampleAgeLine')}</p>
          <p
            dir="ltr"
            className="overflow-x-auto rounded-md border border-border bg-muted px-3 py-2 text-start font-mono text-xs tabular-nums text-foreground"
          >
            {ageTrace?.error
              ? `${ageTrace.expression} — ${t(`equations.errorCodes.${ageTrace.error.code}`)}`
              : `${ageTrace?.substituted ?? ''} = ${run.outputs.age ?? '—'}`}
          </p>
        </div>

        <DefinitionList
          items={KEY_OUTPUTS.map(({ id, unit }) => {
            const value = run.outputs[id];

            return {
              label: t(`equations.outputs.${id}`),
              value:
                value === null || value === undefined ? (
                  <span className="text-muted-foreground">{t('equations.notCalculated')}</span>
                ) : (
                  <span
                    dir="ltr"
                    className="inline-block font-mono text-sm tabular-nums"
                  >
                    {formatEquationValue(value, unit, unitLabel(unit))}
                  </span>
                ),
            };
          })}
        />

        <p className="text-xs text-muted-foreground">{activeValues ? t('equations.sampleTemplateActiveNote') : t('equations.sampleTemplateNote')}</p>
      </section>

      <section className="rounded-lg border border-border bg-card p-5">
        <button
          type="button"
          aria-expanded={isInputsOpen}
          onClick={() => setIsInputsOpen((open) => !open)}
          className="flex w-full items-center gap-2 text-start text-sm font-semibold text-card-foreground transition-colors hover:text-primary"
        >
          {isInputsOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          {t('equations.availableInputs')}
          <span className="ms-auto text-xs font-normal text-muted-foreground">{inputs.length}</span>
        </button>

        {isInputsOpen ? (
          <ul className="mt-4 space-y-3">
            {inputs.map((input) => {
              const value = built.inputs[input.name];

              return (
                <li
                  key={input.name}
                  className="border-b border-border pb-3 last:border-b-0 last:pb-0"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <code
                      dir="ltr"
                      className="font-mono text-xs text-foreground"
                    >
                      {input.name}
                    </code>
                    <Badge mono>{t(`equations.units.${input.unit}`)}</Badge>
                    <Badge>{t(`equations.inputSources.${input.source}`)}</Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{input.description}</p>
                  <p className="mt-1 font-mono text-xs tabular-nums text-muted-foreground">
                    {value === undefined ? t('equations.inputMissing') : `${t('equations.inputSample')}: ${value}`}
                  </p>
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>
    </div>
  );
};
